// 🔧 entry_reg.js
// ✅ Purpose: Admin-facing page for registering or updating items
// ✅ Features:
// - Add/edit items using stored slugs with backwards-compatible document-ID routing
// - Allows explicit manager-authorized slug changes while preserving old URL aliases
// - Auto-assign regcnt for new entries within an event
// - Upload/delete image and set hasImage flag
// - Show success and error notifications
// - Global admins can match owners by email; managers can save unlinked contact details
// - Reset image file input after use

import { createManagerEntry, resolveEntryCategory } from "../../services/entry_registration";
import React, { useEffect, useState, useRef } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import {
  doc,
  getDocs,
  updateDoc,
  serverTimestamp,
  collection,
  getDoc,
  query,
  where,
  limit,
} from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL, deleteObject } from "firebase/storage";
import { httpsCallable } from "firebase/functions";
import { db, storage, functions } from "../../firebase";
import imageCompression from "browser-image-compression";
import { useCategories } from "../../hooks/use_categories";
import { useVotingSettings } from "../../hooks/use_voting_settings";
import FullPageSpinner from "../../components/FullPageSpinner";
import { logger } from '../../utils/logger';
import { useEventContext } from "../../hooks/use_event_context";
import PromotionSourceButton from "../../promotion/components/promotion_source_button";
import { useRole } from "../../role_context";
import { getEntryPath, getEntryEditPath, isValidEntrySlug, resolveEntrySnapshot } from "../../utils/entry_urls";
import {
  hasPurchasedAddOn,
} from "../../utils/event_addons";

const EntryReg = () => {

  const { id } = useParams();
  const {
    eventslug,
    eventId,
    eventDoc,
    eventLoading,
    ready,
  } = useEventContext({ redirectIfNotFound: true });
  const navigate = useNavigate();
  const location = useLocation();
  const { role } = useRole();
  const { settings, loading: settingsLoading } = useVotingSettings(eventId);
  const { categories, loading: catsLoading } = useCategories(eventId);

  // ✅ Silent Auction feature flag
  const auctionEnabled = hasPurchasedAddOn(
    eventDoc,
    "auction"
  );

  const [makeAuctionItem, setMakeAuctionItem] = useState(false);
  const [imgUrl, setImgUrl] = useState("");
  const [imgFile, setImgFile] = useState(null);
  const [carDocId, setCarDocId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState("");
  const [error, setError] = useState("");
  const [slugInput, setSlugInput] = useState("");
  const [savedSlug, setSavedSlug] = useState("");
  const [noticeBusy, setNoticeBusy] = useState(false);
  const fileInputRef = useRef(null);
  const pendingNewEntryNoticeRef = useRef(null);

  const [car, setCar] = useState({
    description: "",
    ownerFirst: "",
    ownerLast: "",
    email: "",
    catid: "",
  });

  useEffect(() => {
    if (!location.state?.entryNotice) return;
    setStatus(location.state.entryNotice.message);
    setError(location.state.entryNotice.warning || "");
  }, [location.state]);

  const notifyCandidate = async (entryId) => {
    const { data } = await httpsCallable(functions, "sendEntryCandidateNotice")({ eventId, entryId });
    if (data.simulated) return "Demo entry saved; candidate email was simulated and not delivered.";
    if (data.sending) return "Candidate notification is already being sent.";
    if (data.alreadySent) return "Candidate notification was already sent.";
    return "Candidate notification email sent.";
  };

  const sendSavedCandidateEmail = async () => {
    if (!carDocId || noticeBusy) return;
    setNoticeBusy(true);
    setError("");
    try {
      setStatus(await notifyCandidate(carDocId));
      if (pendingNewEntryNoticeRef.current === carDocId) pendingNewEntryNoticeRef.current = null;
    }
    catch (noticeError) {
      logger.warn("Candidate email delivery failed", { code: noticeError.code || "unknown" });
      setError("The entry is saved, but the candidate email could not be sent. Use Send Candidate Email to retry.");
    } finally { setNoticeBusy(false); }
  };

  useEffect(() => {
    if (!status && !error) return undefined;
    const timer = setTimeout(() => { setStatus(""); setError(""); }, 20000);
    return () => clearTimeout(timer);
  }, [status, error]);

  // 🔍 Load existing car if regcnt is present
  useEffect(() => {
    if (!id || !eventId) return;

    const fetchCar = async () => {
      setLoading(true);
      try {
        // The edit route accepts the current slug, a retained alias, or the original ID.
        const snap = await resolveEntrySnapshot(db, eventId, id);
    
        if (snap) {
          const carDoc = snap.data();
          setCar({
            ...carDoc,
            ownerEmail: carDoc.ownerEmail || carDoc.email || "",
            cid: snap.id, // ✅ Set canonical cid
            catid: carDoc.catid != null ? carDoc.catid : "", // Set to empty string if missing 
          });

          setCarDocId(snap.id);
          setSlugInput(carDoc.entrySlug || "");
          setSavedSlug(carDoc.entrySlug || "");
    
          if (carDoc.imgUrl) setImgUrl(carDoc.imgUrl);
        } else {
          setStatus(`❌ No entry found for ID: ${id}`);
          setCarDocId(null);
        }
      } catch (err) {
        logger.error("🔥 Error loading car:", {
          value: err
        });
        setError("🔥 Error loading car.");
      } finally {
        setLoading(false);
      }
    };
    
    fetchCar();
  }, [id, eventId]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setCar((prev) => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (e) => {
    setImgFile(e.target.files[0]);
  };

  const handleDeleteImage = async () => {
    if (!imgUrl || !eventId || !carDocId) return;

    const path = `items/${eventId}/${carDocId}.jpg`;
    try {
      await deleteObject(ref(storage, path));
      await updateDoc(doc(db, "events", eventId, "cars", carDocId), {
        imgUrl: "",
        hasImage: false,
      });
      setImgUrl("");
      setImgFile(null);
      if (fileInputRef.current) fileInputRef.current.value = "";
      setStatus("✅ Image deleted.");
    } catch (err) {
      logger.error("❌ Failed to delete image:", {
        value: err
      });
      setError("❌ Error deleting image.");
    }

  };

  const uploadImage = async (docId) => {
    const path = `items/${eventId}/${docId}.jpg`;
    const storageRef = ref(storage, path);
  
    try {
      // ✅ Resize image before upload
      const compressed = await imageCompression(imgFile, {
        maxWidthOrHeight: 1600,
        useWebWorker: true,
      });
  
      await uploadBytes(storageRef, compressed);
      const url = await getDownloadURL(storageRef);
  
      if (fileInputRef.current) fileInputRef.current.value = "";
      return url;
    } catch (err) {
      logger.error("❌ Error during image compression/upload:", {
        value: err
      });
      throw err;
    }
  };
  
  const handleSubmit = async () => {
    setLoading(true);
    setStatus("");
    setError("");

    logger.log("catid: ",{
      value: car.catid
    });
  
    // ✅ Validate required fields with better error messages
    const missingFields = [];
    if (!car.ownerFirst) missingFields.push("Owner First Name");
    if (!car.ownerLast) missingFields.push("Owner Last Name");
    if (!car.email) missingFields.push("Email");
    if (!car.description) missingFields.push("Description");
    if (categories.some((category) => !category.isDefault) && (!car.catid || Number(car.catid) === 0)) missingFields.push("Category");

    if (missingFields.length > 0) {
      setError(`❌ Missing required: ${missingFields.join(", ")}`);
      setLoading(false);
      return;
    }

    const requestedSlug = slugInput.trim();
    if (requestedSlug && !isValidEntrySlug(requestedSlug)) {
      setError("Use 1–100 lowercase letters, numbers, and single hyphens for the entry slug.");
      setLoading(false);
      return;
    }
    // Slug reservations are server-owned and must not be spread into browser writes.
    const writableCar = { ...car };
    delete writableCar.entrySlug;

    try {
      const categoryId = await resolveEntryCategory(eventId, car.catid);
      let docRef;
      let canonicalSlug = savedSlug;
      // Preserve owner identity; a signed-in manager is not automatically the entrant.
      let ownerIdToUse = car.ownerId || "";
      if (car.email.trim().toLowerCase() !== String(car.ownerEmail || "").toLowerCase()) ownerIdToUse = "";
      // Global profile searches require global admin. Managers can register an
      // unlinked entrant by contact details without reading private user profiles.
      if (car.email && role === "admin") {
        const matches = await getDocs(query(collection(db, "users"), where("email", "==", car.email.trim().toLowerCase()), limit(1)));
        if (!matches.empty) ownerIdToUse = matches.docs[0].id;
        else if (car.email.trim().toLowerCase() !== String(car.ownerEmail || "").toLowerCase()) ownerIdToUse = "";
      }

      if (carDocId) {
        if (!savedSlug || requestedSlug !== savedSlug) {
          const updateEntrySlug = httpsCallable(functions, "updateEntrySlug");
          const result = await updateEntrySlug({ eventId, entryId: carDocId, slug: requestedSlug });
          canonicalSlug = result.data.entrySlug;
          setSavedSlug(result.data.entrySlug);
          setSlugInput(result.data.entrySlug);
          setCar((previous) => ({ ...previous, entrySlug: result.data.entrySlug }));
        }
        // ✅ Update existing car
        docRef = doc(db, "events", eventId, "cars", carDocId);
        await updateDoc(docRef, {
          ...writableCar,
          catid: categoryId,
          ownerId: ownerIdToUse || "",
          ownerEmail: car.email || "",
          updatedAt: serverTimestamp(),
        });
      } else {
        // 🆕 New car — assign next regcnt
        const newCar = {
          ...writableCar,
          // Registration service assigns cid and regcnt in the creation transaction.
          catid: categoryId,
          eventId: eventId,
          ownerId: ownerIdToUse || "",
          ownerEmail: car.email || "",
          hasImage: false,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
          description: car.description,
          isAuctionItem: false,
          auctionItemId: "",
        };
          
        const registration = await createManagerEntry(eventId, newCar);
        docRef = registration.entryRef;
        const nextRegCnt = registration.regcnt;
        setCarDocId(docRef.id);
        pendingNewEntryNoticeRef.current = docRef.id;

        // Reserve the URL before reporting success; the background trigger may
        // not have run yet. The existing callable is idempotent on retries.
        const slugResult = await httpsCallable(functions, "updateEntrySlug")({ eventId, entryId: docRef.id });
        canonicalSlug = slugResult.data.entrySlug;
        setSavedSlug(canonicalSlug);
        setSlugInput(canonicalSlug);

        // Email failure never turns a saved registration into another create.
        let notificationMessage = "";
        let notificationWarning = "";
        try {
          notificationMessage = await notifyCandidate(docRef.id);
          pendingNewEntryNoticeRef.current = null;
        }
        catch (noticeError) {
          logger.warn("Candidate email delivery failed", { code: noticeError.code || "unknown" });
          notificationWarning = "The entry is saved, but the candidate email could not be sent. Use Send Candidate Email to retry.";
        }

        // =====================================================
        // 🏷️ Auto-create linked auction item
        // =====================================================

        if (auctionEnabled && makeAuctionItem) {
          logger.log("🏷️ Creating linked auction item", {
            value: docRef.id,
          });

          const createAuctionItem = httpsCallable(functions, "createAuctionItem");
          const auctionResult = await createAuctionItem({
            eventId,
            sourceType: "entry",
            linkedEntryId: docRef.id,
            title: car.description || "Auction Entry",
            description: car.description || "",
            startingBidCents: 0,
            minimumIncrementCents: 500,
            quantityAvailable: 1,
          });

          logger.log("✅ Auction item created", {
            value: auctionResult?.data?.auctionItemId || "",
          });
        }

        if (imgFile) {
          const url = await uploadImage(docRef.id);
          await updateDoc(docRef, {
            imgUrl: url,
            hasImage: true,
          });
          setImgUrl(url);
        }

        // ✅ Clear form
        const savedEntry = await getDoc(docRef);
        const savedEditPath = getEntryEditPath(eventslug, { id: docRef.id, entrySlug: canonicalSlug || savedEntry.data()?.entrySlug });
        setCar({
          description: "",
          ownerFirst: "",
          ownerLast: "",
          email: "",
          catid: "",
        });
        setImgFile(null);
        setImgUrl("");
        setCarDocId(null);
        setSlugInput("");
        setSavedSlug("");
        if (fileInputRef.current) fileInputRef.current.value = "";

        const savedMessage = `✅ Saved successfully. Assigned Reg #${nextRegCnt}. ${notificationMessage}`.trim();
        setStatus(savedMessage);
        setError(notificationWarning);
        setTimeout(() => {
          navigate(savedEditPath, { state: { entryNotice: { message: savedMessage, warning: notificationWarning } } });
        }, 300);
        return;
      }
  
      // ✅ Upload or update image if needed
      if (imgFile) {
        const url = await uploadImage(docRef.id);
        await updateDoc(docRef, {
          imgUrl: url,
          hasImage: true,
        });
        setImgUrl(url);
      }
  
      let notificationMessage = "";
      // Creation may have succeeded before a slug/image failure. Finish the
      // original notification on save retry without registering another entry.
      if (pendingNewEntryNoticeRef.current === carDocId) {
        try {
          notificationMessage = await notifyCandidate(carDocId);
          pendingNewEntryNoticeRef.current = null;
        } catch (noticeError) {
          logger.warn("Candidate email delivery failed", { code: noticeError.code || "unknown" });
          setError("The entry is saved, but the candidate email could not be sent. Use Send Candidate Email to retry.");
        }
      }
      setStatus(`✅ Saved successfully. ${notificationMessage}`.trim());
      const editPath = getEntryEditPath(eventslug, { id: carDocId, entrySlug: canonicalSlug });
      if (id !== (canonicalSlug || carDocId)) navigate(editPath, { replace: true });
    } catch (err) {
      const expected = ["functions/already-exists", "functions/invalid-argument", "functions/not-found"].includes(err?.code);
      const log = expected ? logger.warn : logger.error;
      log("❌ Error saving:", {
        value: err
      });
      setError(err?.message || "Unable to save this entry.");
    }
  
    setLoading(false);
  };
  
  const handleAddNew = () => {
    pendingNewEntryNoticeRef.current = null;
    setCar({
      description: "",
      ownerFirst: "",
      ownerLast: "",
      email: "",
      catid: "",
    });
    setImgFile(null);
    setImgUrl("");
    setCarDocId(null);
    setSlugInput("");
    setSavedSlug("");
    setStatus("");
    setError("");
    if (fileInputRef.current) fileInputRef.current.value = "";
    navigate(`/${eventslug}/entry_reg`);
  };


  
  if (!ready || eventLoading || catsLoading || settingsLoading) {
    return <FullPageSpinner message="Loading Categories and Settings..." />;
  }

  if (eventLoading) return <FullPageSpinner message="Loading Entry Events..." />;

  return (
    <section id="top-content" className="container-fluid py-5">
      <div className="row mb-4">
        <div className="col text-start">
          <h1 className="display-5">🚘 {id ? `Edit Item #${car.regcnt}` : "Register New Item"}</h1>
          <p className="lead text-muted">Manage show entries and upload images</p>
          <PromotionSourceButton source="entries" sourceId={carDocId} />
        </div>
      </div>

      <div className="white-section p-4 rounded shadow-sm bg-white">
        {status && <div className="alert alert-success">{status}</div>}
        {error && <div className="alert alert-danger">{error}</div>}
        {carDocId && <div className="border rounded p-3 mb-3">
          <div className="fw-bold">Candidate Notification</div>
          <p className="form-text mb-2">Email the candidate a private link to review their saved entry and optionally create an account. Save changes before sending; the saved candidate email is used.</p>
          <button type="button" className="btn btn-outline-primary" onClick={sendSavedCandidateEmail}
            disabled={loading || noticeBusy || !car.email?.trim()}>{noticeBusy ? "Sending..." : "Send Candidate Email"}</button>
        </div>}

        <button className="btn btn-outline-secondary mb-3" onClick={handleAddNew}>
          ➕ Add Entry
        </button>

        <div className="row g-3">
          <div className="col-md-4">
            <label className="form-label">Owner First Name</label>
            <input
              type="text"
              name="ownerFirst"
              aria-label="Owner First Name"
              className="form-control"
              value={car.ownerFirst}
              onChange={handleChange}
            />
          </div>
          <div className="col-md-4">
            <label className="form-label">Owner Last Name</label>
            <input
              type="text"
              name="ownerLast"
              aria-label="Owner Last Name"
              className="form-control"
              value={car.ownerLast}
              onChange={handleChange}
            />
          </div>
          <div className="col-md-4">
            <label className="form-label">Email</label>
            <input
              type="email"
              name="email"
              aria-label="Email"
              className="form-control"
              value={car.email}
              onChange={handleChange}
            />
          </div>

          <div className="col-md-6">
            <label className="form-label">Description</label>
            <input
              type="text"
              name="description"
              aria-label="Description"
              className="form-control"
              value={car.description}
              onChange={handleChange}
            />
            {settings?.descriptionTip && (
              <div className="text-muted small mt-1">
                💡 {settings.descriptionTip}
              </div>
            )}
          </div>

          <div className="col-12">
            <label className="form-label" htmlFor="entry-url-slug">Entry URL Slug</label>
            <input
              id="entry-url-slug"
              name="entrySlug"
              type="text"
              className="form-control"
              value={slugInput}
              onChange={(event) => setSlugInput(event.target.value)}
              maxLength={100}
              disabled={!carDocId || loading}
              aria-describedby="entry-url-help"
              placeholder="Generated from Description after the first save"
            />
            <div id="entry-url-help" className="form-text">
              {carDocId ? "Choose a unique URL using lowercase letters, numbers, and single hyphens. Save to apply it. Leave blank to keep the assigned slug." : "Save the entry first to generate its slug, then edit it here if needed."}
              {" "}Description edits do not change the URL. Previous slug and ID links keep working; voting and QR ballot links are unchanged.
            </div>
            {carDocId && <>
              <div className="small text-break mt-2">URL preview: <code>{window.location.origin}{getEntryPath(eventslug, { id: carDocId, entrySlug: slugInput.trim() || savedSlug })}</code></div>
              <a className="small" href={getEntryPath(eventslug, { id: carDocId, entrySlug: savedSlug })} target="_blank" rel="noopener noreferrer">View saved entry</a>
            </>}
          </div>

          <div className="col-md-4">
            <label className="form-label">Category</label>
            <select
              aria-label="Category"
              className="form-select"
              value={car.catid !== undefined && car.catid !== null ? String(car.catid) : ""}
              onChange={(e) => setCar({ ...car, catid: e.target.value })}

            >
              <option value="">{categories.some((category) => !category.isDefault) ? "Select a category" : "No category"}</option>
              {!categories.some((category) => category.isDefault) && categories.some((category) => !category.isDefault) && (
                <option value="__default__">No category (default)</option>
              )}
                {categories
                  // 🚫 Exclude init/placeholder categories and any malformed category records
                  .filter((cat) => Number(cat.id) !== 0 && (cat.isDefault || String(cat.name || "").trim()))
                  // ✅ Sort safely even if a category name is missing
                  .sort((a, b) =>
                    String(a.name || "").localeCompare(String(b.name || ""))
                  )
                  .map((cat) => (
                    <option key={cat.id} value={cat.id}>
                      {cat.isDefault ? "No category" : cat.name}
                    </option>
                ))}
            </select>
          </div>

          <div className="col-md-6">
            <label className="form-label">Upload Image</label>
            <input
              type="file"
              accept="image/*"
              className="form-control"
              onChange={handleFileChange}
              ref={fileInputRef}
            />
          </div>

          {imgUrl && (
            <div className="col-md-6">
              <label className="form-label">Current Image</label>
              <div className="border p-2 rounded">
                <img
                  src={imgUrl}
                  alt="Uploaded Item"
                  style={{
                    maxWidth: "100%",
                    marginBottom: "0.5rem",
                    borderRadius: "6px"
                  }}
                />

                <button
                  type="button"
                  className="btn btn-sm btn-outline-danger"
                  onClick={handleDeleteImage}
                >
                  🗑️ Delete Image
                </button>
              </div>
            </div>
          )}
        </div>

        {/* ✅ Silent Auction Section */}
        {auctionEnabled && (
          <div className="card mt-4 mb-3">
            <div className="card-header">
              <strong>Silent Auction</strong>
            </div>

            <div className="card-body">
              <div className="form-check">
                <input
                  className="form-check-input"
                  type="checkbox"
                  id="makeAuctionItem"
                  checked={makeAuctionItem}
                  onChange={(e) => setMakeAuctionItem(e.target.checked)}
                />

                <label
                  className="form-check-label"
                  htmlFor="makeAuctionItem"
                >
                  Also make this entry a Silent Auction item
                </label>
              </div>

              <div className="small text-muted mt-2">
                Auction pricing, bidding rules, and auction status will be configured
                later from the Auction Admin page.
              </div>
            </div>
          </div>
        )}

        <div className="mt-4 d-flex gap-3">
          <button
            className="btn btn-primary"
            onClick={handleSubmit}
            disabled={loading}
          >
            {loading ? "Saving..." : "💾 Save"}
          </button>
        </div>

      </div>
    </section>
  );
};

export default EntryReg;
